// App Constants

export const APP_NAME = 'Zero Moblt';

export const ROUTES = {
  WELCOME: '/',
  VERIFY_OTP: '/verify-otp',
  STUDENT_DETAILS: '/student-details',
  PARENT_DETAILS: '/parent-details',
  PICKUP_LOCATION: '/pickup-location',
  SETUP_SERVICE: '/setup-service',
  REVIEW_BOOKING: '/review-booking',
  REVIEW_DETAILS: '/review-details',
  EDIT_DETAILS: '/edit-details',
  USER_DETAILS: '/user-details',
  SELECT_PLANS: '/select-plans',
  CHOOSE_PLAN: '/choose-plan',
  PAYMENT_PROCESSING: '/payment-processing',
  PAYMENT_SUCCESS: '/payment-success',
  PAYMENT_FAILURE: '/payment-failure',
  SUBSCRIPTION_DETAILS: '/subscription',
  FAQ: '/faq',
} as const;

export const GENDERS = [
  { value: 'M', label: 'Boy' },
  { value: 'F', label: 'Girl' },
  { value: 'NA', label: 'Other' },
] as const;

export enum Grade {
  CLASS_1 = 'CLASS_1',
  CLASS_2 = 'CLASS_2',
  CLASS_3 = 'CLASS_3',
  CLASS_4 = 'CLASS_4',
  CLASS_5 = 'CLASS_5',
  CLASS_6 = 'CLASS_6',
  CLASS_7 = 'CLASS_7',
  CLASS_8 = 'CLASS_8',
  CLASS_9 = 'CLASS_9',
  CLASS_10 = 'CLASS_10',
  CLASS_11 = 'CLASS_11',
  CLASS_12 = 'CLASS_12',
  NEET_DROPPER = 'NEET_DROPPER',
  JEE_DROPPER = 'JEE_DROPPER',
}

export const GRADE_OPTIONS = Object.values(Grade).map(grade => ({
  value: grade,
  label: grade.replace(/_/g, ' '),
}));

// ADM-47: only Class 7-12 are currently served. Class 1-6, NEET Dropper, and JEE Dropper are
// excluded from selection, but Grade/GRADE_OPTIONS above are kept intact so existing students
// already recorded under a removed option still display correctly wherever their stored grade
// is looked up.
const SELECTABLE_GRADES = [
  Grade.CLASS_7,
  Grade.CLASS_8,
  Grade.CLASS_9,
  Grade.CLASS_10,
  Grade.CLASS_11,
  Grade.CLASS_12,
];

export const SELECTABLE_GRADE_OPTIONS = GRADE_OPTIONS.filter(option =>
  SELECTABLE_GRADES.includes(option.value as Grade)
);

export const ADULT_GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
] as const;

export const GRADES = [
  'Nursery',
  'LKG',
  'UKG',
  'Grade 1',
  'Grade 2',
  'Grade 3',
  'Grade 4',
  'Grade 5',
  'Grade 6',
  'Grade 7',
  'Grade 8',
  'Grade 9',
  'Grade 10',
  'Grade 11',
  'Grade 12',
] as const;

export const CAMPUSES = [
  'DPS Gurgaon',
  'DPS Whitefield',
  'DPS Bangalore',
  'DPS Delhi',
  'Other',
] as const;

export const SHIFTS = [
  { value: 'morning', label: 'Morning (7:30 AM)', time: '7:30 AM' },
  { value: 'afternoon', label: 'Afternoon (1:30 PM)', time: '1:30 PM' },
] as const;

export const TRANSPORT_TYPES = [
  {
    value: 'ac',
    label: 'AC Transport',
    description: 'Climate controlled comfort',
  },
  {
    value: 'non-ac',
    label: 'Non-AC Transport',
    description: 'Well-ventilated & economical',
  },
] as const;

export const PAYMENT_PLANS = {
  ANNUAL: {
    id: 'annual',
    name: 'One Time Payment Plan',
    description: 'Lowest overall cost for the full academic year',
    baseAmount: 45238,
    gst: 8143,
    discount: 12000,
    totalAmount: 41381,
    tokenAmount: 2500,
    installments: 2,
    badge: 'Best Value',
  },
  TERM: {
    id: 'term',
    name: 'Part Payment Plan',
    description: 'More flexibility, higher total cost',
    tokenAmount: 2500,
    firstPayment: 28750,
    secondPayment: 28750,
    installments: 3,
    badge: 'Flexible Plan',
  },
} as const;

export const DID_YOU_KNOW_FACTS = [
  "Omni's seating capacity is 5 including the driver. Anything more is non compliant & illegal.",
  "Your number is secured with enterprise-grade encryption and never shared with third parties.",
  "Our AI optimizes pickup times and routes based on your child's grade and campus location.",
  "Most low-cost options overload vehicles to cut costs. Zero Moblt follows compliant seating, verified drivers, and fixed routes.",
  "You can add multiple children and manage all their transport needs from this dashboard.",
] as const;

export const SAFETY_FEATURES = [
  {
    title: 'Legal Seating',
    description: 'Compliant capacity',
  },
  {
    title: 'Verified Drivers',
    description: 'Background checked',
  },
  {
    title: 'Fixed Routes',
    description: 'Optimized paths',
  },
] as const;

export const OTP_LENGTH = 6;
export const OTP_RESEND_TIME = 30; // seconds
export const PHONE_NUMBER_LENGTH = 10;

// ── Subscription / Installment ────────────────────────────────────────────────

/**
 * Number of days before an installment due date at which the due banner appears
 * in the UI (soft reminder window).
 *
 * Intentionally wider than the backend PENDING_DUES threshold (3 days):
 * - Frontend shows the banner at 14 days → parent sees a soft reminder early
 * - Backend flips student to PENDING_DUES at 3 days → service is suspended if unpaid
 *
 * These are two separate thresholds with different purposes — do not conflate them.
 */
export const INSTALLMENT_DUE_DAYS = 14;

// ── Routing Availability ──────────────────────────────────────────────────────
export const START_DATE_BUFFER_DAYS = 7;

export const ROUTING_AVAILABILITY_DISCLAIMER = {
  title: 'Route Assignment Status : Waitlisted',
  points: [
    'All routes are currently full. Service start dates can only be selected from the following week.',
    'This allows us to plan routes effectively and ensure reliable service.',
    'All registrations will remain waitlisted and pending route assignment.',
    'Payment does not guarantee a route for 5–7 days.',
  ],
  /** Flat string for contexts that need a single line (e.g. meta tags). */
  flat: 'All routes are currently full. Service start dates can only be selected from the following week. This allows us to plan routes effectively and ensure reliable service. All registrations will remain waitlisted and pending route assignment. Payment does not guarantee a route for 5-7 days.',
};